import { Component, OnInit, signal, computed, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { SpacesService } from '@generated/services/spaces.service';
import { InvitesService } from '@generated/services/invites.service';
import { NotificationService } from '@core/services/notification.service';
import { AuthService } from '@core/services/auth.service';
import { SpaceService } from '@core/services/space.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { SpaceDto, SpaceUserDto, UpdateSpaceRequest, UpdateSpaceResult, InviteDto, SpaceRole } from '@generated/models';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { SPACE_FIELDS } from '@core/assistant/assistant-catalog';

@Component({
  selector: 'app-space-edit',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    TableModule,
    TagModule,
    SelectModule,
    ConfirmDialogModule,
    TooltipModule,
    DeleteConfirmationComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './space-edit.html',
  styleUrl: './space-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpaceEditComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = SPACE_FIELDS;

  private fb = inject(FormBuilder);
  private spacesService = inject(SpacesService);
  private invitesService = inject(InvitesService);
  private notificationService = inject(NotificationService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  private spaceService = inject(SpaceService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  editForm!: FormGroup;
  loading = signal(true);
  saving = signal(false);
  space = signal<SpaceDto | null>(null);
  spaceId: string;
  maxNameLength = 50;
  maxDescriptionLength = 200;

  // Invites
  invites = signal<InviteDto[]>([]);
  invitesLoading = signal(false);
  inviteSearch = signal('');
  showInviteForm = signal(false);
  sendingInvite = signal(false);
  inviteEmail = '';
  inviteRole: SpaceRole = 'Maintainer';
  roleOptions: { label: string; value: SpaceRole }[] = [
    { label: 'Maintainer', value: 'Maintainer' }
  ];

  // Delete workspace
  showDeleteDialog = signal(false);
  isDeleting = signal(false);

  // Remove member
  showRemoveMemberDialog = signal(false);
  isRemovingMember = signal(false);
  memberToRemove: SpaceUserDto | null = null;

  get isDefaultOwnerSpace(): boolean {
    const s = this.space();
    return !!s?.is_default && s?.user_role === 'Owner';
  }

  filteredInvites = computed(() => {
    const search = this.inviteSearch().toLowerCase();
    if (!search) return this.invites();
    return this.invites().filter(i => i.email?.toLowerCase().includes(search));
  });

  constructor() {
    this.spaceId = this.route.snapshot.params['id'];
  }

  ngOnInit() {
    this.editForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(this.maxNameLength)]],
      description: ['', [Validators.maxLength(this.maxDescriptionLength)]]
    });

    this.loadSpace();
    this.loadInvites();
  }

  loadSpace() {
    console.log('Loading space with ID:', this.spaceId);
    this.loading.set(true);
    
    this.spacesService.apiSpacesIdGet({ id: this.spaceId }).subscribe({
      next: (space: SpaceDto) => {
        this.space.set(space);
        this.editForm.patchValue({
          name: space.name,
          description: space.description || ''
        });
        // Auto-select this space in the sidebar selector (without page reload)
        if (this.spaceService.getCurrentSpaceId() !== space.id) {
          this.spaceService.setCurrentSpace(space, false);
        }
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading space:', error);
        this.loading.set(false);
        this.router.navigate(['/spaces']);
      }
    });
  }


  get nameControl() {
    return this.editForm.get('name');
  }

  get descriptionControl() {
    return this.editForm.get('description');
  }

  get nameLength(): number {
    return this.nameControl?.value?.length || 0;
  }

  get descriptionLength(): number {
    return this.descriptionControl?.value?.length || 0;
  }

  goBack() {
    this.router.navigate(['/spaces']);
  }

  onSubmit() {
    if (this.editForm.valid && !this.saving()) {
      this.saving.set(true);
      
      const updateData: UpdateSpaceRequest = {
        name: this.nameControl?.value,
        description: this.descriptionControl?.value || null
      };

      this.spacesService.apiSpacesIdPut({ id: this.spaceId, body: updateData }).subscribe({
        next: (space: UpdateSpaceResult) => {
          console.log('Space updated:', space);
          this.saving.set(false);
          this.notificationService.showSuccess('Workspace updated successfully');
          this.goBack();
        },
        error: (error: any) => {
          console.error('Error updating space:', error);
          this.saving.set(false);
        }
      });
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  // Invites methods
  loadInvites() {
    this.invitesLoading.set(true);
    this.invitesService.apiInvitesSpaceSpaceIdGet({ spaceId: this.spaceId }).subscribe({
      next: (invites) => {
        this.invites.set(invites);
        this.invitesLoading.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.invitesLoading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  isExpired(invite: InviteDto): boolean {
    return invite.status === 'Sent' && !!invite.valid_to && new Date(invite.valid_to) < new Date();
  }

  getDisplayStatus(invite: InviteDto): string {
    if (this.isExpired(invite)) return 'Expired';
    return invite.status || '';
  }

  getStatusSeverity(invite: InviteDto): 'success' | 'warn' | 'danger' | 'secondary' {
    if (this.isExpired(invite)) return 'secondary';
    switch (invite.status) {
      case 'Sent': return 'warn';
      case 'Fulfilled': return 'success';
      case 'Revoked': return 'danger';
      default: return 'secondary';
    }
  }

  copyInviteLink(invite: InviteDto) {
    if (invite.invite_link) {
      navigator.clipboard.writeText(invite.invite_link).then(() => {
        this.notificationService.showSuccess('Invite link copied');
      });
    }
  }

  onDeleteClick() {
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirm() {
    this.isDeleting.set(true);
    this.spacesService.apiSpacesIdDelete({ id: this.spaceId }).subscribe({
      next: () => {
        this.notificationService.showSuccess('Workspace deleted successfully');
        this.showDeleteDialog.set(false);
        this.spaceService.loadSpaces();
        this.router.navigate(['/spaces']);
      },
      error: (error: any) => {
        console.error('Error deleting space:', error);
        this.notificationService.showError('Failed to delete workspace');
        this.isDeleting.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  toggleInviteForm() {
    this.showInviteForm.update(v => !v);
    if (!this.showInviteForm()) {
      this.inviteEmail = '';
      this.inviteRole = 'Maintainer';
    }
    this.cdr.markForCheck();
  }

  sendInvite(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Send invite to ${this.inviteEmail} with role ${this.inviteRole}?`,
      header: 'Confirm Invite',
      icon: 'pi pi-envelope',
      accept: () => {
        this.sendingInvite.set(true);
        this.invitesService.apiInvitesPost({
          body: {
            space_id: this.spaceId,
            email: this.inviteEmail,
            role: this.inviteRole
          }
        }).subscribe({
          next: () => {
            this.notificationService.showSuccess('Invite sent successfully');
            this.sendingInvite.set(false);
            this.showInviteForm.set(false);
            this.inviteEmail = '';
            this.inviteRole = 'Maintainer';
            this.loadInvites();
          },
          error: () => {
            this.notificationService.showError('Failed to send invite');
            this.sendingInvite.set(false);
          }
        });
      }
    });
  }

  removeMember(user: SpaceUserDto) {
    this.memberToRemove = user;
    this.showRemoveMemberDialog.set(true);
  }

  onRemoveMemberConfirm() {
    if (!this.memberToRemove) return;
    const user = this.memberToRemove;
    const isSelf = user.user_id === this.authService.getUserProfile()?.id;
    this.isRemovingMember.set(true);

    this.spacesService.apiSpacesIdUsersUserIdDelete({ id: this.spaceId, userId: user.user_id! }).subscribe({
      next: () => {
        this.notificationService.showSuccess('Member removed');
        this.showRemoveMemberDialog.set(false);
        this.isRemovingMember.set(false);
        this.memberToRemove = null;
        if (isSelf) {
          this.spaceService.loadSpaces();
          this.router.navigate(['/spaces']);
        } else {
          this.loadSpace();
        }
      },
      error: () => {
        this.notificationService.showError('Failed to remove member');
        this.isRemovingMember.set(false);
      }
    });
  }

  revokeInvite(invite: InviteDto, event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Revoke invite for ${invite.email}?`,
      header: 'Confirm Revoke',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.invitesService.apiInvitesInviteIdRevokePost({ inviteId: invite.id! }).subscribe({
          next: () => {
            this.notificationService.showSuccess('Invite revoked');
            this.loadInvites();
          },
          error: () => {
            this.notificationService.showError('Failed to revoke invite');
          }
        });
      }
    });
  }
}